import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Target, Settings, Shield, Zap, Brain, Clock, Bell, CheckCircle, Lock, Wifi, Code, Database, Filter, Globe, Search, FolderOpen, Building2, Loader2, Router, Cloud, AlertTriangle, EyeOff } from 'lucide-react';
import CGUModal from '../CGUModal';
import SubdomainDiscoveryModal from '../SubdomainDiscoveryModal';
import VPNConnectionPanel from '../VPNConnectionPanel';
import FreePlanWarning from '../FreePlanWarning';
import ContractErrorModal from '../ContractErrorModal';
import apiClient from '../../api/client';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useAuthStore from '../../store/authStore';

// Normalize target: strip protocol and trailing slashes/paths
const normalizeTarget = (target) => {
  if (!target) return '';
  let t = target.trim();
  t = t.replace(/^https?:\/\//i, '');
  t = t.split('/')[0];
  t = t.replace(/\.$/, '');
  return t;
};

// Function to detect if IP is public (not private)
const isPublicIP = (target) => {
  if (!target) return false;
  const trimmed = normalizeTarget(target);

  // Extract IP from CIDR if needed
  const ipPart = trimmed.split('/')[0];

  // Check if it's an IP address pattern
  const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = ipPart.match(ipPattern);

  if (!match) {
    // Check if it's a domain - domains are considered public
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    return domainPattern.test(trimmed);
  }

  const [, octet1, octet2, octet3, octet4] = match.map(Number);

  // Validate IP octets
  if (octet1 > 255 || octet2 > 255 || octet3 > 255 || octet4 > 255) return false;

  // Private IP ranges (RFC 1918)
  // 10.0.0.0 - 10.255.255.255
  if (octet1 === 10) return false;

  // 172.16.0.0 - 172.31.255.255
  if (octet1 === 172 && octet2 >= 16 && octet2 <= 31) return false;

  // 192.168.0.0 - 192.168.255.255
  if (octet1 === 192 && octet2 === 168) return false;

  // Loopback 127.0.0.0 - 127.255.255.255
  if (octet1 === 127) return false;

  // Link-local 169.254.0.0 - 169.254.255.255
  if (octet1 === 169 && octet2 === 254) return false;

  // It's a public IP
  return true;
};

const ExpertScanWizard = ({ isOpen, onClose, onComplete }) => {
  // Get active workspace from context
  const { activeWorkspace, showSelector, setShowSelector } = useWorkspace();
  // Get user info for plan restrictions
  const { user } = useAuthStore();

  // Start at step 2 if workspace is already selected
  const [currentStep, setCurrentStep] = useState(activeWorkspace?.id ? 2 : 1);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [showSubdomainModal, setShowSubdomainModal] = useState(false);
  const [detectedDomain, setDetectedDomain] = useState(null);
  const [selectedSubdomains, setSelectedSubdomains] = useState([]);
  const [vpnStatus, setVpnStatus] = useState(null);
  const [isTargetPublic, setIsTargetPublic] = useState(false);
  // Contract error modal state
  const [contractError, setContractError] = useState({
    isOpen: false,
    errorType: null,
    message: '',
    workspaceId: null,
    target: ''
  });
  // Contract override options (for retrying scan with skip or specific contract)
  const [contractOverride, setContractOverride] = useState({
    skip_contract_check: false,
    contract_id: null
  });
  const [formData, setFormData] = useState({
    // Scan Name
    name: '', // Optional scan name

    // Workspace & VPN
    workspace_id: '',
    use_vpn: false,
    vpn_config_id: '',
    vpn_username: '',
    vpn_password: '',

    // Target Configuration
    target_mode: 'single',
    single_target: '',
    cidr_target: '',
    range_start: '',
    range_end: '',
    targets_file: null,
    exclude_targets: '',

    // Scan Type
    scan_type: 'full_autonomous',
    
    // Nmap Advanced Configuration
    nmap_enabled: true,
    nmap_scan_technique: 'syn',
    nmap_port_mode: 'top1000',
    nmap_custom_ports: '',
    nmap_timing: 'T4',
    nmap_host_discovery: true,
    nmap_os_detection: true,
    nmap_version_detection: true,
    nmap_script_scan: false,
    nmap_custom_scripts: '',
    nmap_aggressive: false,
    nmap_stealth_mode: false,
    nmap_fragment_packets: false,
    nmap_decoy_scan: false,
    nmap_decoys: '',
    nmap_source_port: '',
    nmap_randomize_hosts: false,
    nmap_max_retries: 3,
    nmap_host_timeout: 900,
    
    // OpenVAS Configuration
    openvas_enabled: true,
    openvas_profile: 'Full and fast',
    openvas_optimize: true,
    openvas_max_hosts: 20,
    openvas_max_checks: 4,
    
    // Vulnerability Scanning
    vuln_scan_enabled: true,
    nikto_enabled: true,
    sqlmap_enabled: true,
    nuclei_enabled: true,
    nuclei_templates: 'all',
    
    // Exploitation
    auto_exploit: false,
    exploit_severity_filter: ['critical', 'high'],
    safe_mode: true,
    max_exploits_per_host: 3,
    exploit_timeout: 300,
    session_persistence: false,
    
    // AI Configuration
    ai_analysis: true,
    ai_model: 'sylink',
    ai_assistance_mode: 'auto', // 'auto' or 'manual'
    ai_depth: 'comprehensive',
    ai_auto_recommendations: true,
    ai_exploit_suggestions: true,
    ai_priority_scoring: true,
    
    // Evasion & Stealth
    stealth_mode: 'none', // none, basic, advanced, paranoid
    evasion_enabled: false,
    ttl_manipulation: false,
    bad_checksum: false,
    data_length_manipulation: false,
    ip_options: '',
    spoof_mac: '',
    
    // Performance & Limits
    max_concurrent_scans: 10,
    bandwidth_limit: 0,
    parallel_hosts: 5,
    timeout_global: 3600,
    
    // Scheduling
    schedule_enabled: false,
    schedule_type: 'once',
    schedule_datetime: '',
    recurrence: 'daily',
    
    // Notifications
    notify_email: false,
    notify_webhook: false,
    email_addresses: '',
    webhook_url: '',
    notify_on_critical: true,
    notify_on_completion: true,
    
    // Output & Reporting
    auto_report: false,
    report_format: 'html',
    include_screenshots: false,
    include_raw_data: false,
    compliance_check: [],

    // Public IP specific attack options
    public_ip_attacks: {
      enabled: false,
      box_firewall: false,
      vpn_server: false,
      default_creds: false,
      firmware_vuln: false,
      management_interface: false,
    },
  });

  // Auto-set workspace_id from active workspace
  useEffect(() => {
    if (activeWorkspace?.id && !formData.workspace_id) {
      setFormData(prev => ({ ...prev, workspace_id: activeWorkspace.id }));
    }
  }, [activeWorkspace?.id]);

  // Callback for VPN status changes from VPNSelector
  const handleVpnStatusChange = useCallback((status) => {
    setVpnStatus(status);
  }, []);

  // Define all steps - step 1 is hidden if workspace is already selected
  const allSteps = [
    { id: 1, title: 'Workspace & VPN', icon: FolderOpen, desc: 'Client & network context' },
    { id: 2, title: 'Target configuration', icon: Target, desc: 'Define targets' },
    { id: 3, title: 'Advanced Nmap', icon: Wifi, desc: 'Port scan options' },
    { id: 4, title: 'Vulnerability scan', icon: Shield, desc: 'OpenVAS & tools' },
    { id: 5, title: 'Exploitation', icon: Zap, desc: 'Auto-exploitation' },
    { id: 6, title: 'IA & Analyse', icon: Brain, desc: 'Configuration IA' },
    { id: 7, title: 'Stealth & Evasion', icon: Lock, desc: 'IDS/IPS evasion' },
    { id: 8, title: 'Performance', icon: Settings, desc: 'Limites & optimisation' },
    { id: 9, title: 'Planification & Notifications', icon: Clock, desc: 'Automatisation' },
    { id: 10, title: 'Review & Launch', icon: CheckCircle, desc: 'Final review' },
  ];

  // Filter out step 1 if workspace is already selected
  const steps = activeWorkspace?.id ? allSteps.filter(s => s.id !== 1) : allSteps;

  // Function to detect if input is a domain (not IP or CIDR)
  const isDomain = (target) => {
    if (!target) return false;
    const trimmed = normalizeTarget(target);
    // IP address patterns
    const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(\/\d{1,2})?$/;
    // CIDR pattern
    const cidrPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
    // Range pattern
    const rangePattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\s*-\s*\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

    if (ipPattern.test(trimmed) || cidrPattern.test(trimmed) || rangePattern.test(trimmed)) {
      return false;
    }
    // Domain pattern: must contain at least one dot and valid characters
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    return domainPattern.test(trimmed);
  };

  // Effect to detect domain when target changes
  useEffect(() => {
    const target = formData.target_mode === 'single' ? formData.single_target : '';
    const normalized = normalizeTarget(target);
    if (isDomain(normalized)) {
      setDetectedDomain(normalized);
    } else {
      setDetectedDomain(null);
      setSelectedSubdomains([]);
    }
  }, [formData.single_target, formData.target_mode]);

  // Effect to detect public IP
  useEffect(() => {
    const target = formData.target_mode === 'single' ? formData.single_target :
                   formData.target_mode === 'cidr' ? formData.cidr_target :
                   formData.target_mode === 'range' ? formData.range_start : '';
    const targetIsPublic = isPublicIP(target);
    setIsTargetPublic(targetIsPublic);
    // Auto-enable public IP attacks panel when public IP detected
    if (targetIsPublic && !formData.public_ip_attacks.enabled) {
      setFormData(prev => ({
        ...prev,
        public_ip_attacks: {
          ...prev.public_ip_attacks,
          enabled: true,
        }
      }));
    }
  }, [formData.single_target, formData.cidr_target, formData.range_start, formData.target_mode]);

  // Handler for subdomain selection
  const handleSubdomainsSelected = (data) => {
    setSelectedSubdomains(data.subdomains);
    // Store the attack configuration
    setFormData(prev => ({
      ...prev,
      subdomain_attack: {
        subdomains: data.subdomains,
        attackMode: data.attackMode,
        aiModel: data.aiModel
      }
    }));
  };

  // Plans requiring contract validation
  const plansRequiringContract = ['free', 'enterprise', 'entreprise', 'groupe', 'group'];
  const userPlan = user?.subscription?.plan?.toLowerCase() || 'free';
  const requiresContract = plansRequiringContract.includes(userPlan);

  // Get current target based on target_mode
  const getCurrentTarget = () => {
    switch (formData.target_mode) {
      case 'single': return formData.single_target;
      case 'cidr': return formData.cidr_target;
      case 'range': return `${formData.range_start}-${formData.range_end}`;
      default: return formData.single_target;
    }
  };

  // Validate contract before proceeding from Target step
  const validateContract = async () => {
    const target = getCurrentTarget();
    if (!requiresContract || !formData.workspace_id || !target) {
      return true; // No validation needed
    }

    try {
      const response = await apiClient.post('/api/v1/contracts/validate-for-scan', {
        workspace_id: formData.workspace_id,
        target: target
      });

      // Check the validation result
      if (response.data.valid) {
        return true;
      }

      // Contract validation failed - show error modal
      const reason = response.data.reason;
      setContractError({
        isOpen: true,
        errorType: reason === 'target_not_in_perimeter' ? 'target_not_in_perimeter' : 'contract_required',
        message: response.data.message,
        workspaceId: formData.workspace_id,
        target: target
      });
      return false;
    } catch (error) {
      // Network or server error - show error
      const detail = error.response?.data?.detail;
      setContractError({
        isOpen: true,
        errorType: 'contract_required',
        message: typeof detail === 'string' ? detail : (detail?.message || 'Erreur de validation du contrat.'),
        workspaceId: formData.workspace_id,
        target: target
      });
      return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate contract only at launch (last step), not during navigation
    if (getCurrentTarget() && formData.workspace_id) {
      const isValid = await validateContract();
      if (!isValid) {
        return; // Stop - contract error modal will be shown
      }
    }
    // Afficher le modal CGU avant de lancer le scan
    setShowCGUModal(true);
  };

  const handleCGUAccept = async (overrideOptions = {}) => {
    try {
      let submitData = { ...formData };

      // Apply contract override options
      if (overrideOptions.skip_contract_check || contractOverride.skip_contract_check) {
        submitData.skip_contract_check = true;
      }
      if (overrideOptions.contract_id || contractOverride.contract_id) {
        submitData.contract_id = overrideOptions.contract_id || contractOverride.contract_id;
      }

      await onComplete(submitData);
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;

      // Check for contract validation errors (403)
      if (error.response?.status === 403 && (detail?.error === 'contract_required' || detail?.error === 'target_not_in_perimeter')) {
        // Show contract error modal with guidance
        const target = formData.target_mode === 'single' ? formData.single_target :
                       formData.target_mode === 'cidr' ? formData.cidr_target :
                       `${formData.range_start}-${formData.range_end}`;
        setContractError({
          isOpen: true,
          errorType: detail.error,
          message: detail.message,
          workspaceId: detail.workspace_id,
          target: target
        });
      // Check for insufficient credits error (402)
      } else if (error.response?.status === 402) {
        if (detail?.error === 'insufficient_credits') {
          alert(`Insufficient credits to launch this scan.\n\nCredits required: ${detail.message || 'N/A'}\n\nPlease purchase credits or upgrade your plan.`);
        } else {
          alert('Credits insuffisants. Veuillez acheter des credits pour continuer.');
        }
      } else {
        alert('Erreur lors de la creation du scan: ' + (typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail || error.message));
      }
    }
  };

  // Handle continue without contract
  const handleContinueWithoutContract = () => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    setContractOverride({ skip_contract_check: true, contract_id: null });
    // Retry scan with skip_contract_check
    handleCGUAccept({ skip_contract_check: true });
  };

  // Handle contract selection
  const handleSelectContract = (contractId) => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    setContractOverride({ skip_contract_check: false, contract_id: contractId });
    // Retry scan with selected contract
    handleCGUAccept({ contract_id: contractId });
  };

  if (!isOpen) return null;

  return (
    <>
    {/* Modal CGU */}
    <CGUModal
      isOpen={showCGUModal}
      onClose={() => setShowCGUModal(false)}
      onAccept={handleCGUAccept}
      scanType="Analyse Experte"
    />

    {/* Modal Subdomain Discovery */}
    <SubdomainDiscoveryModal
      isOpen={showSubdomainModal}
      onClose={() => setShowSubdomainModal(false)}
      domain={detectedDomain}
      onSubdomainsSelected={handleSubdomainsSelected}
      aiModel={formData.ai_model}
      scanType={formData.scan_type}
    />

    {/* Modal Contract Error */}
    <ContractErrorModal
      isOpen={contractError.isOpen}
      onClose={() => {
        setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
      }}
      errorType={contractError.errorType}
      message={contractError.message}
      workspaceId={contractError.workspaceId}
      target={contractError.target}
      onContinueWithoutContract={handleContinueWithoutContract}
      onSelectContract={handleSelectContract}
    />

    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-brand-black-light border-2 border-brand-green/30 rounded-2xl max-w-7xl w-full my-4 flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20 sticky top-0 bg-brand-black-light z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gradient-green">Assistant d'Analyse Experte</h2>
              <p className="text-gray-400 mt-1">Advanced configuration for penetration testing professionals</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-black rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-brand-green" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center min-w-[80px]">
                  <div
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all text-xs ${
                      currentStep >= step.id
                        ? 'border-brand-green bg-brand-green/20 text-brand-green'
                        : 'border-gray-700 text-gray-600'
                    }`}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span className={`text-[10px] mt-1 text-center ${currentStep >= step.id ? 'text-brand-green' : 'text-gray-600'}`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 min-w-[20px] ${currentStep > step.id ? 'bg-brand-green' : 'bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Free Plan Warning - shown on all steps */}
          <FreePlanWarning compact className="mb-4" />

          {/* Step 1: Workspace & VPN */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-gradient-to-r from-brand-green/10 to-blue-500/10 border border-brand-green/30 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-bold text-brand-green flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Contexte de l'Analyse Experte
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  Choose where to store results and configure the network environment
                </p>
              </div>

              {/* Active Workspace Display */}
              <div className="bg-brand-black rounded-xl p-5 border-2 border-brand-green/30">
                <label className="block text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-brand-green" />
                  Client / Workspace
                </label>

                {activeWorkspace ? (
                  <div className="p-4 rounded-lg border-2 border-brand-green bg-brand-green/10">
                    <div className="font-semibold text-white mb-1 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-brand-green" />
                      {activeWorkspace.company_name || activeWorkspace.name}
                      <CheckCircle className="w-4 h-4 text-brand-green" />
                    </div>
                    <div className="text-xs text-gray-400">
                      {activeWorkspace.description || 'Workspace actif'}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Scans will be stored in this workspace. Use the selector at the top of the page to change.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500 border-2 border-orange-500/30 rounded-lg bg-orange-500/5">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-orange-400" />
                    <p className="text-orange-400 font-medium">No workspace selected</p>
                    <p className="text-xs mt-1 text-gray-400">Select a workspace to continue</p>
                    <button
                      type="button"
                      onClick={() => setShowSelector(true)}
                      className="mt-3 px-4 py-2 bg-brand-green text-brand-black rounded-lg font-medium hover:bg-brand-green/90 transition-colors text-sm"
                    >
                      Select a workspace
                    </button>
                  </div>
                )}
              </div>

              {/* VPN Connection Panel */}
              <VPNConnectionPanel
                formData={formData}
                setFormData={setFormData}
                showWorkspaceSelector={false}
              />
            </div>
          )}

          {/* Step 2: Target Configuration */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Scan Name (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Nom du scan (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Audit Trimestriel Q1 2025, Pentest Client XYZ..."
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Give a custom name to easily identify this scan
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Mode de ciblage</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'single', label: 'Single host', icon: '🎯' },
                    { value: 'cidr', label: 'Plage CIDR', icon: '🌐' },
                    { value: 'range', label: 'Plage IP', icon: '📊' },
                    { value: 'file', label: 'Fichier cibles', icon: '📄' }
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, target_mode: mode.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.target_mode === mode.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="text-2xl mb-1">{mode.icon}</div>
                      <div className="text-xs text-white font-medium">{mode.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {formData.target_mode === 'single' && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Target IP or hostname *</label>
                  <input
                    type="text"
                    value={formData.single_target}
                    onChange={(e) => {
                      const val = e.target.value;
                      const normalized = /^https?:\/\//i.test(val) ? normalizeTarget(val) : val;
                      setFormData({ ...formData, single_target: normalized });
                    }}
                    placeholder="192.168.1.100 ou target.exemple.com"
                    className="input-field w-full"
                  />

                  {/* Domain Detection - Subdomain Discovery Button */}
                  {detectedDomain && (
                    <div className="mt-4 p-4 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-brand-green" />
                          <div>
                            <p className="text-sm text-brand-green font-medium">Domain detected</p>
                            <p className="text-xs text-gray-400">
                              Discover subdomains of <span className="font-mono">{detectedDomain}</span>
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowSubdomainModal(true)}
                          className="px-4 py-2 bg-brand-green text-brand-black rounded-lg font-medium hover:bg-brand-green/90 transition-colors flex items-center gap-2 text-sm"
                        >
                          <Search className="w-4 h-4" />
                          Discover subdomains
                        </button>
                      </div>

                      {/* Show selected subdomains count */}
                      {selectedSubdomains.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-brand-green/20">
                          <p className="text-xs text-gray-400">
                            <span className="text-brand-green font-bold">{selectedSubdomains.length}</span> subdomains selected for the attack
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedSubdomains.slice(0, 5).map((sub, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-brand-green/20 text-brand-green text-xs rounded">
                                {sub.subdomain}
                              </span>
                            ))}
                            {selectedSubdomains.length > 5 && (
                              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                                +{selectedSubdomains.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {formData.target_mode === 'cidr' && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Notation CIDR *</label>
                  <input
                    type="text"
                    value={formData.cidr_target}
                    onChange={(e) => setFormData({ ...formData, cidr_target: e.target.value })}
                    placeholder="192.168.1.0/24"
                    className="input-field w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">Exemples : 10.0.0.0/8, 192.168.0.0/16, 172.16.0.0/12</p>
                </div>
              )}

              {formData.target_mode === 'range' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Start IP *</label>
                    <input
                      type="text"
                      value={formData.range_start}
                      onChange={(e) => setFormData({ ...formData, range_start: e.target.value })}
                      placeholder="192.168.1.1"
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">IP de fin *</label>
                    <input
                      type="text"
                      value={formData.range_end}
                      onChange={(e) => setFormData({ ...formData, range_end: e.target.value })}
                      placeholder="192.168.1.254"
                      className="input-field w-full"
                    />
                  </div>
                </div>
              )}

              {formData.target_mode === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Fichier de cibles (une IP par ligne)</label>
                  <input
                    type="file"
                    accept=".txt,.csv"
                    onChange={(e) => setFormData({ ...formData, targets_file: e.target.files[0] })}
                    className="input-field w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">Upload a .txt or .csv file with one target per line</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Exclure des cibles (Optionnel)</label>
                <input
                  type="text"
                  value={formData.exclude_targets}
                  onChange={(e) => setFormData({ ...formData, exclude_targets: e.target.value })}
                  placeholder="192.168.1.1,192.168.1.254 ou 192.168.1.0/28"
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">IPs or CIDR to exclude, separated by commas</p>
              </div>

              {/* Public IP Detection - Box/Firewall/VPN Attack Options */}
              {isTargetPublic && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    <div>
                      <p className="text-sm text-orange-400 font-medium">Public IP/Domain Detected</p>
                      <p className="text-xs text-gray-400">
                        Target exposed on the Internet - Specific attack options available
                      </p>
                    </div>
                  </div>

                  {/* Attack Options */}
                  <div className="space-y-3">
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                      Types d'attaques Box / Firewall / VPN
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Box/Firewall Scanner */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={formData.public_ip_attacks.box_firewall}
                          onChange={(e) => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              ...formData.public_ip_attacks,
                              box_firewall: e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Router className="w-4 h-4 text-orange-400" />
                            <span className="text-sm font-medium text-white">Box / Firewall / Router</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Routify, RouterSploit, Nmap firewall scripts, CVE check
                          </p>
                        </div>
                      </label>

                      {/* VPN Server Scanner */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={formData.public_ip_attacks.vpn_server}
                          onChange={(e) => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              ...formData.public_ip_attacks,
                              vpn_server: e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-medium text-white">VPN Server</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            OpenVPN, IPSec, WireGuard, Fortinet, Palo Alto CVEs
                          </p>
                        </div>
                      </label>

                      {/* Default Credentials */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={formData.public_ip_attacks.default_creds}
                          onChange={(e) => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              ...formData.public_ip_attacks,
                              default_creds: e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-red-400" />
                            <span className="text-sm font-medium text-white">Default credentials</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Default admin, manufacturer accounts, SSH/Telnet
                          </p>
                        </div>
                      </label>

                      {/* Firmware Vulnerabilities */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={formData.public_ip_attacks.firmware_vuln}
                          onChange={(e) => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              ...formData.public_ip_attacks,
                              firmware_vuln: e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Cloud className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium text-white">Firmware vulnerabilities</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            CVE firmware, backdoors, buffer overflow, RCE
                          </p>
                        </div>
                      </label>

                      {/* Management Interface */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all col-span-full">
                        <input
                          type="checkbox"
                          checked={formData.public_ip_attacks.management_interface}
                          onChange={(e) => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              ...formData.public_ip_attacks,
                              management_interface: e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-cyan-400" />
                            <span className="text-sm font-medium text-white">Interface de Management Web</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Scan exposed web admin interfaces (HTTPS/HTTP), FortiGate, pfSense, OPNsense, Cisco ASDM
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Quick Select All */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500">
                        {Object.values(formData.public_ip_attacks).filter(v => v === true).length - (formData.public_ip_attacks.enabled ? 1 : 0)} option(s) selected
                      </span>
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          public_ip_attacks: {
                            enabled: true,
                            box_firewall: true,
                            vpn_server: true,
                            default_creds: true,
                            firmware_vuln: true,
                            management_interface: true,
                          }
                        })}
                        className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                      >
                        Select all
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Scan Type</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'network_discovery', title: 'Network discovery', desc: 'Quick reconnaissance', time: '5-15 min' },
                    { value: 'vulnerability_scan', title: 'Vulnerability scan', desc: 'Analyse CVE approfondie', time: '30-60 min' },
                    { value: 'full_autonomous', title: 'Autonome complet', desc: 'Pentest complet', time: '1-3 h' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, scan_type: type.value })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        formData.scan_type === type.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="font-semibold text-white mb-1">{type.title}</div>
                      <div className="text-xs text-gray-500">{type.desc}</div>
                      <div className="text-xs text-brand-green mt-1">⏱ {type.time}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Nmap Advanced */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="nmap_enabled"
                  checked={formData.nmap_enabled}
                  onChange={(e) => setFormData({ ...formData, nmap_enabled: e.target.checked })}
                  className="w-5 h-5 accent-brand-green"
                />
                <label htmlFor="nmap_enabled" className="text-lg font-bold text-brand-green cursor-pointer">
                  Activer le scan de ports Nmap
                </label>
              </div>

              {formData.nmap_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Technique de scan</label>
                    <select
                      value={formData.nmap_scan_technique}
                      onChange={(e) => setFormData({ ...formData, nmap_scan_technique: e.target.value })}
                      className="input-field w-full"
                    >
                      <option value="syn">SYN Stealth Scan (-sS) [Recommended]</option>
                      <option value="connect">TCP Connect Scan (-sT)</option>
                      <option value="ack">ACK Scan (-sA)</option>
                      <option value="window">Window Scan (-sW)</option>
                      <option value="maimon">Maimon Scan (-sM)</option>
                      <option value="null">NULL Scan (-sN)</option>
                      <option value="fin">FIN Scan (-sF)</option>
                      <option value="xmas">XMAS Scan (-sX)</option>
                      <option value="udp">UDP Scan (-sU)</option>
                      <option value="sctp">SCTP INIT Scan (-sY)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Port specification</label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                      {[
                        { value: 'top100', label: 'Top 100' },
                        { value: 'top1000', label: 'Top 1000' },
                        { value: 'all', label: 'Tous (65535)' },
                        { value: 'common', label: 'Courants' },
                        { value: 'custom', label: 'Custom' }
                      ].map((port) => (
                        <button
                          key={port.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, nmap_port_mode: port.value })}
                          className={`p-2 rounded-lg border-2 transition-all text-xs ${
                            formData.nmap_port_mode === port.value
                              ? 'border-brand-green bg-brand-green/10 text-white'
                              : 'border-gray-800 hover:border-brand-green/50 text-gray-400'
                          }`}
                        >
                          {port.label}
                        </button>
                      ))}
                    </div>
                    {formData.nmap_port_mode === 'custom' && (
                      <input
                        type="text"
                        value={formData.nmap_custom_ports}
                        onChange={(e) => setFormData({ ...formData, nmap_custom_ports: e.target.value })}
                        placeholder="80,443,8080-8090,3389"
                        className="input-field w-full"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Timing Template</label>
                    <div className="grid grid-cols-6 gap-2">
                      {['T0', 'T1', 'T2', 'T3', 'T4', 'T5'].map((timing, idx) => (
                        <button
                          key={timing}
                          type="button"
                          onClick={() => setFormData({ ...formData, nmap_timing: timing })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.nmap_timing === timing
                              ? 'border-brand-green bg-brand-green/10'
                              : 'border-gray-800 hover:border-brand-green/50'
                          }`}
                        >
                          <div className={`font-bold text-sm ${formData.nmap_timing === timing ? 'text-white' : 'text-gray-400'}`}>
                            {timing}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {['Paranoid', 'Sneaky', 'Polite', 'Normal', 'Aggressive', 'Insane'][idx]}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_os_detection"
                        checked={formData.nmap_os_detection}
                        onChange={(e) => setFormData({ ...formData, nmap_os_detection: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_os_detection" className="text-sm text-gray-300">
                        <div className="font-medium">OS detection (-O)</div>
                        <div className="text-xs text-gray-500">Identify operating systems</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_version_detection"
                        checked={formData.nmap_version_detection}
                        onChange={(e) => setFormData({ ...formData, nmap_version_detection: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_version_detection" className="text-sm text-gray-300">
                        <div className="font-medium">Version detection (-sV)</div>
                        <div className="text-xs text-gray-500">Detect service versions</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_script_scan"
                        checked={formData.nmap_script_scan}
                        onChange={(e) => setFormData({ ...formData, nmap_script_scan: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_script_scan" className="text-sm text-gray-300">
                        <div className="font-medium">Scripts NSE (-sC)</div>
                        <div className="text-xs text-gray-500">Run default scripts</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_aggressive"
                        checked={formData.nmap_aggressive}
                        onChange={(e) => setFormData({ ...formData, nmap_aggressive: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_aggressive" className="text-sm text-gray-300">
                        <div className="font-medium">Scan agressif (-A)</div>
                        <div className="text-xs text-gray-500">OS, version, traceroute</div>
                      </label>
                    </div>
                  </div>

                  {formData.nmap_script_scan && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Custom NSE Scripts (Optional)</label>
                      <input
                        type="text"
                        value={formData.nmap_custom_scripts}
                        onChange={(e) => setFormData({ ...formData, nmap_custom_scripts: e.target.value })}
                        placeholder="http-enum,smb-enum-shares,dns-zone-transfer"
                        className="input-field w-full"
                      />
                      <p className="text-xs text-gray-500 mt-2">Comma-separated NSE script names</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Max Retries</label>
                      <input
                        type="number"
                        value={formData.nmap_max_retries}
                        onChange={(e) => setFormData({ ...formData, nmap_max_retries: parseInt(e.target.value) })}
                        min="0"
                        max="10"
                        className="input-field w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Host Timeout (seconds)</label>
                      <input
                        type="number"
                        value={formData.nmap_host_timeout}
                        onChange={(e) => setFormData({ ...formData, nmap_host_timeout: parseInt(e.target.value) })}
                        min="60"
                        max="7200"
                        className="input-field w-full"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Vulnerability Scan */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="openvas_enabled"
                    checked={formData.openvas_enabled}
                    onChange={(e) => setFormData({ ...formData, openvas_enabled: e.target.checked })}
                    className="w-5 h-5 accent-brand-green"
                  />
                  <label htmlFor="openvas_enabled" className="text-sm text-gray-300 cursor-pointer flex-1">
                    <div className="font-medium text-lg">Enable OpenVAS Vulnerability Scanning</div>
                    <div className="text-xs text-gray-500">Deep CVE-based vulnerability detection with OpenVAS/GVM</div>
                  </label>
                </div>
              </div>

              {formData.openvas_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Profil de scan OpenVAS</label>
                    <select
                      value={formData.openvas_profile}
                      onChange={(e) => setFormData({ ...formData, openvas_profile: e.target.value })}
                      className="input-field w-full"
                    >
                      <option value="Full and fast">Full and fast (Recommended)</option>
                      <option value="Full and deep">Complet et approfondi</option>
                      <option value="Full and deep ultimate">Complet et approfondi ultime (Couverture maximale)</option>
                      <option value="System Discovery">System discovery (Light)</option>
                      <option value="Host Discovery">Host discovery (Quick)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Max simultaneous hosts</label>
                      <input
                        type="number"
                        value={formData.openvas_max_hosts}
                        onChange={(e) => setFormData({ ...formData, openvas_max_hosts: parseInt(e.target.value) })}
                        min="1"
                        max="50"
                        className="input-field w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Max simultaneous checks</label>
                      <input
                        type="number"
                        value={formData.openvas_max_checks}
                        onChange={(e) => setFormData({ ...formData, openvas_max_checks: parseInt(e.target.value) })}
                        min="1"
                        max="10"
                        className="input-field w-full"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Additional vulnerability tools</label>
                <div className="space-y-3">
                  {[
                    { id: 'nikto_enabled', label: 'Nikto', desc: 'Scanner de serveur web' },
                    { id: 'sqlmap_enabled', label: 'SQLMap', desc: 'SQL injection detection' },
                    { id: 'nuclei_enabled', label: 'Nuclei', desc: 'Template-based scan' }
                  ].map((tool) => (
                    <div key={tool.id} className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id={tool.id}
                        checked={formData[tool.id]}
                        onChange={(e) => setFormData({ ...formData, [tool.id]: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor={tool.id} className="text-sm text-gray-300">
                        <div className="font-medium">{tool.label}</div>
                        <div className="text-xs text-gray-500">{tool.desc}</div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {formData.nuclei_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Nuclei Templates</label>
                  <select
                    value={formData.nuclei_templates}
                    onChange={(e) => setFormData({ ...formData, nuclei_templates: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="all">All Templates</option>
                    <option value="cves">CVEs Only</option>
                    <option value="exposed-panels">Exposed Panels</option>
                    <option value="vulnerabilities">Vulnerabilities</option>
                    <option value="misconfigurations">Misconfigurations</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Exploitation */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-500 mt-0.5">⚠️</div>
                  <div className="flex-1">
                    <div className="font-semibold text-yellow-500 mb-1">Exploitation Warning</div>
                    <div className="text-xs text-gray-400">
                      Enabling automatic exploitation will attempt to actively exploit discovered vulnerabilities. 
                      Only use with proper written authorization. Unauthorized access is illegal.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="auto_exploit"
                    checked={formData.auto_exploit}
                    onChange={(e) => setFormData({ ...formData, auto_exploit: e.target.checked })}
                    className="w-5 h-5 accent-brand-green"
                  />
                  <label htmlFor="auto_exploit" className="text-sm text-gray-300 cursor-pointer flex-1">
                    <div className="font-medium text-lg">Activer l'exploitation automatique</div>
                    <div className="text-xs text-gray-500">Attempt to automatically exploit critical and high severity vulnerabilities</div>
                  </label>
                </div>
              </div>

              {formData.auto_exploit && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Filter by severity</label>
                    <div className="space-y-2">
                      {[
                        { value: 'critical', label: 'Critique', color: 'red-500' },
                        { value: 'high', label: 'High', color: 'orange-500' },
                        { value: 'medium', label: 'Moyenne', color: 'yellow-500' },
                        { value: 'low', label: 'Faible', color: 'blue-500' }
                      ].map((severity) => (
                        <div key={severity.value} className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`severity-${severity.value}`}
                            checked={formData.exploit_severity_filter.includes(severity.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  exploit_severity_filter: [...formData.exploit_severity_filter, severity.value]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  exploit_severity_filter: formData.exploit_severity_filter.filter(s => s !== severity.value)
                                });
                              }
                            }}
                            className="w-4 h-4 accent-brand-green"
                          />
                          <label htmlFor={`severity-${severity.value}`} className="text-sm text-gray-300 capitalize cursor-pointer flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full bg-${severity.color}`}></div>
                            {severity.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Max exploits per host</label>
                      <input
                        type="number"
                        value={formData.max_exploits_per_host}
                        onChange={(e) => setFormData({ ...formData, max_exploits_per_host: parseInt(e.target.value) })}
                        min="1"
                        max="10"
                        className="input-field w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Exploit timeout (seconds)</label>
                      <input
                        type="number"
                        value={formData.exploit_timeout}
                        onChange={(e) => setFormData({ ...formData, exploit_timeout: parseInt(e.target.value) })}
                        min="60"
                        max="1800"
                        className="input-field w-full"
                      />
                    </div>
                  </div>

                  <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                    <input
                      type="checkbox"
                      id="safe_mode"
                      checked={formData.safe_mode}
                      onChange={(e) => setFormData({ ...formData, safe_mode: e.target.checked })}
                      className="w-4 h-4 accent-brand-green mr-3"
                    />
                    <label htmlFor="safe_mode" className="text-sm text-gray-300">
                      <div className="font-medium">Safe mode</div>
                      <div className="text-xs text-gray-500">Avoid exploits that could crash services or cause data loss</div>
                    </label>
                  </div>

                  <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                    <input
                      type="checkbox"
                      id="session_persistence"
                      checked={formData.session_persistence}
                      onChange={(e) => setFormData({ ...formData, session_persistence: e.target.checked })}
                      className="w-4 h-4 accent-brand-green mr-3"
                    />
                    <label htmlFor="session_persistence" className="text-sm text-gray-300">
                      <div className="font-medium">Installer la persistance de session</div>
                      <div className="text-xs text-gray-500">Maintain access even after restart (requires authorization)</div>
                    </label>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 6: AI & Analysis */}
          {currentStep === 6 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="ai_analysis"
                    checked={formData.ai_analysis}
                    onChange={(e) => setFormData({ ...formData, ai_analysis: e.target.checked })}
                    className="w-5 h-5 accent-brand-green"
                  />
                  <label htmlFor="ai_analysis" className="text-sm text-gray-300 cursor-pointer flex-1">
                    <div className="font-medium text-lg">Enable AI-assisted analysis</div>
                    <div className="text-xs text-gray-500">Use LLMs for intelligent vulnerability evaluation and exploitation recommendations</div>
                  </label>
                </div>
              </div>

              {formData.ai_analysis && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">AI model selection</label>
                    <select
                      value={formData.ai_model}
                      onChange={(e) => setFormData({ ...formData, ai_model: e.target.value })}
                      className="input-field w-full"
                    >
                      <option value="sylink">SYLink AI (Recommended - Security optimized)</option>
                      <option value="sylink-thinking">SYLink Thinking 32B (Analyse approfondie)</option>
                      <option value="deepseek-r1">DeepSeek R1 (Raisonnement approfondi)</option>
                      <option value="qwen3">Qwen3 (Analyse rapide)</option>
                      <option value="mistral">Mistral (Rapide)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Mode d'assistance IA</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, ai_assistance_mode: 'auto' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.ai_assistance_mode === 'auto'
                            ? 'border-brand-green bg-brand-green/10'
                            : 'border-gray-800 hover:border-brand-green/50'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'auto' ? 'bg-brand-green/20' : 'bg-gray-800'}`}>
                            <Brain className="w-5 h-5 text-brand-green" />
                          </div>
                          <div className="font-semibold text-white text-lg">Auto</div>
                        </div>
                        <div className="text-xs text-gray-500 text-left">
                          L'IA analyse automatiquement les resultats et prend des decisions en temps reel. Intervention minimale requise.
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, ai_assistance_mode: 'manual' })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.ai_assistance_mode === 'manual'
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-gray-800 hover:border-blue-500/50'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'manual' ? 'bg-blue-500/20' : 'bg-gray-800'}`}>
                            <Settings className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="font-semibold text-white text-lg">Manuel</div>
                        </div>
                        <div className="text-xs text-gray-500 text-left">
                          L'IA fournit des suggestions et analyses mais vous gardez le controle total. Validation requise a chaque etape.
                        </div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Profondeur d'analyse</label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'quick', label: 'Rapide', desc: 'Analyse basique (~1 min)', color: 'blue' },
                        { value: 'standard', label: 'Standard', desc: 'Balanced (~5 min)', color: 'green' },
                        { value: 'comprehensive', label: 'Comprehensive', desc: 'In-depth analysis (~15 min)', color: 'purple' }
                      ].map((depth) => (
                        <button
                          key={depth.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, ai_depth: depth.value })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.ai_depth === depth.value
                              ? 'border-brand-green bg-brand-green/10'
                              : 'border-gray-800 hover:border-brand-green/50'
                          }`}
                        >
                          <div className="font-semibold text-white text-sm mb-1">{depth.label}</div>
                          <div className="text-xs text-gray-500">{depth.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="ai_auto_recommendations"
                        checked={formData.ai_auto_recommendations}
                        onChange={(e) => setFormData({ ...formData, ai_auto_recommendations: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="ai_auto_recommendations" className="text-sm text-gray-300">
                        <div className="font-medium">Auto Remediation Recommendations</div>
                        <div className="text-xs text-gray-500">AI suggests fixes for each vulnerability</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="ai_exploit_suggestions"
                        checked={formData.ai_exploit_suggestions}
                        onChange={(e) => setFormData({ ...formData, ai_exploit_suggestions: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="ai_exploit_suggestions" className="text-sm text-gray-300">
                        <div className="font-medium">AI Exploit Suggestions</div>
                        <div className="text-xs text-gray-500">Generate custom exploit code</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="ai_priority_scoring"
                        checked={formData.ai_priority_scoring}
                        onChange={(e) => setFormData({ ...formData, ai_priority_scoring: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="ai_priority_scoring" className="text-sm text-gray-300">
                        <div className="font-medium">AI Priority Scoring</div>
                        <div className="text-xs text-gray-500">Contextual risk assessment</div>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 7: Stealth & Evasion */}
          {currentStep === 7 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-red-500 mt-0.5">🚨</div>
                  <div className="flex-1">
                    <div className="font-semibold text-red-400 mb-1">Advanced evasion techniques</div>
                    <div className="text-xs text-gray-400">
                      These options help avoid IDS/IPS detection but may reduce scan accuracy. Use only if you understand the implications.
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stealth Mode Selector */}
              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-3">
                  <EyeOff className="w-4 h-4 text-brand-green" />
                  Mode Furtif Rapide
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'none', label: 'None', desc: 'Fast scan, detectable', icon: '⚡' },
                    { value: 'basic', label: 'Basic', desc: 'Fragmented packets', icon: '🔒' },
                    { value: 'advanced', label: 'Advanced', desc: 'Slow timing + spoofing', icon: '🛡️' },
                    { value: 'paranoid', label: 'Paranoid', desc: 'Max evasion (very slow)', icon: '👻' },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, stealth_mode: mode.value })}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.stealth_mode === mode.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{mode.icon}</span>
                        <span className="font-semibold text-white text-sm">{mode.label}</span>
                      </div>
                      <div className="text-xs text-gray-500">{mode.desc}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Select a preset mode or enable the advanced options below for fine control
                </p>
              </div>

              <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                <input
                  type="checkbox"
                  id="evasion_enabled"
                  checked={formData.evasion_enabled}
                  onChange={(e) => setFormData({ ...formData, evasion_enabled: e.target.checked })}
                  className="w-5 h-5 accent-brand-green mr-3"
                />
                <label htmlFor="evasion_enabled" className="text-sm text-gray-300">
                  <div className="font-medium text-lg">Advanced Evasion Options</div>
                  <div className="text-xs text-gray-500">Configuration fine des techniques IDS/IPS</div>
                </label>
              </div>

              {formData.evasion_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_fragment_packets"
                        checked={formData.nmap_fragment_packets}
                        onChange={(e) => setFormData({ ...formData, nmap_fragment_packets: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_fragment_packets" className="text-sm text-gray-300">
                        <div className="font-medium">Fragmentation de paquets (-f)</div>
                        <div className="text-xs text-gray-500">Split packets to bypass filters</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_decoy_scan"
                        checked={formData.nmap_decoy_scan}
                        onChange={(e) => setFormData({ ...formData, nmap_decoy_scan: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_decoy_scan" className="text-sm text-gray-300">
                        <div className="font-medium">Scan leurre (-D)</div>
                        <div className="text-xs text-gray-500">Use decoy IPs to mask the real source</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="ttl_manipulation"
                        checked={formData.ttl_manipulation}
                        onChange={(e) => setFormData({ ...formData, ttl_manipulation: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="ttl_manipulation" className="text-sm text-gray-300">
                        <div className="font-medium">Manipulation TTL (--ttl)</div>
                        <div className="text-xs text-gray-500">Custom packet time-to-live</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="bad_checksum"
                        checked={formData.bad_checksum}
                        onChange={(e) => setFormData({ ...formData, bad_checksum: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="bad_checksum" className="text-sm text-gray-300">
                        <div className="font-medium">Checksum invalide (--badsum)</div>
                        <div className="text-xs text-gray-500">Contourner les filtres de checksum simples</div>
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                      <input
                        type="checkbox"
                        id="nmap_randomize_hosts"
                        checked={formData.nmap_randomize_hosts}
                        onChange={(e) => setFormData({ ...formData, nmap_randomize_hosts: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-3"
                      />
                      <label htmlFor="nmap_randomize_hosts" className="text-sm text-gray-300">
                        <div className="font-medium">Randomize host order</div>
                        <div className="text-xs text-gray-500">Scan targets in random order</div>
                      </label>
                    </div>
                  </div>

                  {formData.nmap_decoy_scan && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">IPs leurres (optionnel)</label>
                      <input
                        type="text"
                        value={formData.nmap_decoys}
                        onChange={(e) => setFormData({ ...formData, nmap_decoys: e.target.value })}
                        placeholder="192.168.1.10,192.168.1.20,ME,192.168.1.30"
                        className="input-field w-full"
                      />
                      <p className="text-xs text-gray-500 mt-2">Use ME to represent your real IP among decoys</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Usurpation de port source (optionnel)</label>
                    <input
                      type="text"
                      value={formData.nmap_source_port}
                      onChange={(e) => setFormData({ ...formData, nmap_source_port: e.target.value })}
                      placeholder="53, 80, 443"
                      className="input-field w-full"
                    />
                    <p className="text-xs text-gray-500 mt-2">Utiliser un port source courant (DNS:53, HTTP:80, HTTPS:443)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Usurpation d'adresse MAC (optionnel)</label>
                    <input
                      type="text"
                      value={formData.spoof_mac}
                      onChange={(e) => setFormData({ ...formData, spoof_mac: e.target.value })}
                      placeholder="00:11:22:33:44:55"
                      className="input-field w-full"
                    />
                    <p className="text-xs text-gray-500 mt-2">Use a manufacturer-specific MAC for better evasion</p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 8: Performance */}
          {currentStep === 8 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Max concurrent scans</label>
                  <input
                    type="number"
                    value={formData.max_concurrent_scans}
                    onChange={(e) => setFormData({ ...formData, max_concurrent_scans: parseInt(e.target.value) })}
                    min="1"
                    max="50"
                    className="input-field w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of parallel scan processes</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">Parallel Hosts</label>
                  <input
                    type="number"
                    value={formData.parallel_hosts}
                    onChange={(e) => setFormData({ ...formData, parallel_hosts: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                    className="input-field w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Hosts scanned simultaneously</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Bandwidth Limit (KB/s)</label>
                <input
                  type="number"
                  value={formData.bandwidth_limit}
                  onChange={(e) => setFormData({ ...formData, bandwidth_limit: parseInt(e.target.value) })}
                  min="0"
                  max="100000"
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">0 = unlimited. Useful for slow connections</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">Global Timeout (seconds)</label>
                <input
                  type="number"
                  value={formData.timeout_global}
                  onChange={(e) => setFormData({ ...formData, timeout_global: parseInt(e.target.value) })}
                  min="300"
                  max="86400"
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">Maximum duration for entire scan (1 hour = 3600)</p>
              </div>
            </div>
          )}

          {/* Step 9: Schedule & Notifications */}
          {currentStep === 9 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                <input
                  type="checkbox"
                  id="schedule_enabled"
                  checked={formData.schedule_enabled}
                  onChange={(e) => setFormData({ ...formData, schedule_enabled: e.target.checked })}
                  className="w-5 h-5 accent-brand-green mr-3"
                />
                <label htmlFor="schedule_enabled" className="text-sm text-gray-300">
                  <div className="font-medium text-lg">Planifier le scan</div>
                  <div className="text-xs text-gray-500">Run the scan at a specific time or recurrently</div>
                </label>
              </div>

              {formData.schedule_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Type de planification</label>
                    <div className="grid grid-cols-3 gap-3">
                      {['once', 'recurring', 'cron'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({ ...formData, schedule_type: type })}
                          className={`p-3 rounded-lg border-2 transition-all capitalize ${
                            formData.schedule_type === type
                              ? 'border-brand-green bg-brand-green/10 text-white'
                              : 'border-gray-800 hover:border-brand-green/50 text-gray-400'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.schedule_type === 'once' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Date et heure</label>
                      <input
                        type="datetime-local"
                        value={formData.schedule_datetime}
                        onChange={(e) => setFormData({ ...formData, schedule_datetime: e.target.value })}
                        className="input-field w-full"
                      />
                    </div>
                  )}

                  {formData.schedule_type === 'recurring' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">Recurrence</label>
                      <select
                        value={formData.recurrence}
                        onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                        className="input-field w-full"
                      >
                        <option value="hourly">Toutes les heures</option>
                        <option value="daily">Quotidien</option>
                        <option value="weekly">Hebdomadaire</option>
                        <option value="monthly">Mensuel</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-4">Notifications</label>
                <div className="space-y-3">
                  <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                    <input
                      type="checkbox"
                      id="notify_email"
                      checked={formData.notify_email}
                      onChange={(e) => setFormData({ ...formData, notify_email: e.target.checked })}
                      className="w-4 h-4 accent-brand-green mr-3"
                    />
                    <label htmlFor="notify_email" className="text-sm text-gray-300">
                      <div className="font-medium">Notifications par email</div>
                      <div className="text-xs text-gray-500">Receive updates by email</div>
                    </label>
                  </div>

                  {formData.notify_email && (
                    <input
                      type="text"
                      value={formData.email_addresses}
                      onChange={(e) => setFormData({ ...formData, email_addresses: e.target.value })}
                      placeholder="admin@example.com, security@example.com"
                      className="input-field w-full"
                    />
                  )}

                  <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                    <input
                      type="checkbox"
                      id="notify_webhook"
                      checked={formData.notify_webhook}
                      onChange={(e) => setFormData({ ...formData, notify_webhook: e.target.checked })}
                      className="w-4 h-4 accent-brand-green mr-3"
                    />
                    <label htmlFor="notify_webhook" className="text-sm text-gray-300">
                      <div className="font-medium">Notifications webhook</div>
                      <div className="text-xs text-gray-500">Send JSON events to the webhook URL</div>
                    </label>
                  </div>

                  {formData.notify_webhook && (
                    <input
                      type="text"
                      value={formData.webhook_url}
                      onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                      placeholder="https://hooks.slack.com/services/..."
                      className="input-field w-full"
                    />
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <input
                        type="checkbox"
                        id="notify_on_critical"
                        checked={formData.notify_on_critical}
                        onChange={(e) => setFormData({ ...formData, notify_on_critical: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-2"
                      />
                      <label htmlFor="notify_on_critical" className="text-xs text-gray-300">
                        Notify critical findings
                      </label>
                    </div>

                    <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
                      <input
                        type="checkbox"
                        id="notify_on_completion"
                        checked={formData.notify_on_completion}
                        onChange={(e) => setFormData({ ...formData, notify_on_completion: e.target.checked })}
                        className="w-4 h-4 accent-brand-green mr-2"
                      />
                      <label htmlFor="notify_on_completion" className="text-xs text-gray-300">
                        Notify at end of scan
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 10: Review & Launch */}
          {currentStep === 10 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-6 border border-brand-green/20">
                <h3 className="text-xl font-bold text-brand-green mb-4">Expert scan configuration summary</h3>
                
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                  {/* Target Config */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> Configuration de la cible
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Mode:</span>
                        <span className="text-white ml-2 capitalize">{formData.target_mode}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Cible :</span>
                        <span className="text-brand-green ml-2 font-mono text-xs">
                          {formData.target_mode === 'single' && formData.single_target}
                          {formData.target_mode === 'cidr' && formData.cidr_target}
                          {formData.target_mode === 'range' && `${formData.range_start} - ${formData.range_end}`}
                          {formData.target_mode === 'file' && formData.targets_file?.name}
                        </span>
                      </div>
                      {formData.exclude_targets && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Exclus :</span>
                          <span className="text-red-400 ml-2 text-xs">{formData.exclude_targets}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Nmap Config */}
                  {formData.nmap_enabled && (
                    <div className="border-t border-gray-800 pt-3">
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Wifi className="w-4 h-4" /> Configuration Nmap
                      </h4>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div><span className="text-gray-500">Technique:</span> <span className="text-white">{formData.nmap_scan_technique.toUpperCase()}</span></div>
                        <div><span className="text-gray-500">Ports:</span> <span className="text-white">{formData.nmap_port_mode}</span></div>
                        <div><span className="text-gray-500">Timing:</span> <span className="text-white">{formData.nmap_timing}</span></div>
                        <div><span className="text-gray-500">OS Detection:</span> <span className={formData.nmap_os_detection ? 'text-brand-green' : 'text-gray-500'}>{formData.nmap_os_detection ? 'Yes' : 'No'}</span></div>
                        <div><span className="text-gray-500">Version :</span> <span className={formData.nmap_version_detection ? 'text-brand-green' : 'text-gray-500'}>{formData.nmap_version_detection ? 'Yes' : 'No'}</span></div>
                        <div><span className="text-gray-500">Scripts :</span> <span className={formData.nmap_script_scan ? 'text-brand-green' : 'text-gray-500'}>{formData.nmap_script_scan ? 'Yes' : 'No'}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Vulnerability Scan */}
                  <div className="border-t border-gray-800 pt-3">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Vulnerability analysis
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">OpenVAS :</span> <span className={formData.openvas_enabled ? 'text-brand-green' : 'text-gray-500'}>{formData.openvas_enabled ? formData.openvas_profile : 'Disabled'}</span></div>
                      <div><span className="text-gray-500">Nikto :</span> <span className={formData.nikto_enabled ? 'text-brand-green' : 'text-gray-500'}>{formData.nikto_enabled ? 'Enabled' : 'Disabled'}</span></div>
                      <div><span className="text-gray-500">Nuclei :</span> <span className={formData.nuclei_enabled ? 'text-brand-green' : 'text-gray-500'}>{formData.nuclei_enabled ? 'Enabled' : 'Disabled'}</span></div>
                    </div>
                  </div>

                  {/* Exploitation */}
                  {formData.auto_exploit && (
                    <div className="border-t border-gray-800 pt-3">
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" /> Exploitation
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-gray-500">Auto exploitation:</span> <span className="text-yellow-500">Enabled</span></div>
                        <div><span className="text-gray-500">Safe mode:</span> <span className={formData.safe_mode ? 'text-brand-green' : 'text-red-400'}>{formData.safe_mode ? 'Yes' : 'No'}</span></div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Severity filter:</span>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {formData.exploit_severity_filter.map(s => (
                              <span key={s} className="px-2 py-0.5 bg-brand-green/20 text-brand-green rounded text-xs capitalize">{s}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI */}
                  {formData.ai_analysis && (
                    <div className="border-t border-gray-800 pt-3">
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" /> Analyse IA
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-gray-500">Model:</span> <span className="text-white">{formData.ai_model}</span></div>
                        <div><span className="text-gray-500">Profondeur :</span> <span className="text-white capitalize">{formData.ai_depth}</span></div>
                        <div><span className="text-gray-500">Recommandations :</span> <span className={formData.ai_auto_recommendations ? 'text-brand-green' : 'text-gray-500'}>{formData.ai_auto_recommendations ? 'Yes' : 'No'}</span></div>
                        <div><span className="text-gray-500">Suggestions d'exploits :</span> <span className={formData.ai_exploit_suggestions ? 'text-brand-green' : 'text-gray-500'}>{formData.ai_exploit_suggestions ? 'Yes' : 'No'}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Stealth & Evasion */}
                  <div className="border-t border-gray-800 pt-3">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Stealth mode and evasion
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <div>
                        <span className="text-gray-500">Mode Furtif:</span>
                        <span className={`ml-2 ${formData.stealth_mode !== 'none' ? 'text-brand-green' : 'text-gray-500'}`}>
                          {formData.stealth_mode === 'none' && 'Aucun'}
                          {formData.stealth_mode === 'basic' && '🔒 Basique'}
                          {formData.stealth_mode === 'advanced' && '🛡️ Advanced'}
                          {formData.stealth_mode === 'paranoid' && '👻 Paranoid'}
                        </span>
                      </div>
                    </div>
                    {formData.evasion_enabled && (
                      <div className="flex gap-2 flex-wrap">
                        {formData.nmap_fragment_packets && <span className="badge bg-red-500/20 text-red-400 text-xs">Fragmentation</span>}
                        {formData.nmap_decoy_scan && <span className="badge bg-red-500/20 text-red-400 text-xs">Leurres</span>}
                        {formData.ttl_manipulation && <span className="badge bg-red-500/20 text-red-400 text-xs">Manip. TTL</span>}
                        {formData.bad_checksum && <span className="badge bg-red-500/20 text-red-400 text-xs">Checksum inv.</span>}
                        {formData.nmap_randomize_hosts && <span className="badge bg-red-500/20 text-red-400 text-xs">Random order</span>}
                      </div>
                    )}
                  </div>

                  {/* Performance */}
                  <div className="border-t border-gray-800 pt-3">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Performances
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">Concurrent:</span> <span className="text-white">{formData.max_concurrent_scans}</span></div>
                      <div><span className="text-gray-500">Parallel hosts:</span> <span className="text-white">{formData.parallel_hosts}</span></div>
                      <div><span className="text-gray-500">Timeout:</span> <span className="text-white">{formData.timeout_global}s</span></div>
                    </div>
                  </div>

                  {/* Notifications */}
                  {(formData.notify_email || formData.notify_webhook) && (
                    <div className="border-t border-gray-800 pt-3">
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Bell className="w-4 h-4" /> Notifications
                      </h4>
                      <div className="space-y-1 text-xs">
                        {formData.notify_email && <div className="text-white">📧 Email: {formData.email_addresses}</div>}
                        {formData.notify_webhook && <div className="text-white">🔗 Webhook configured</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-blue-500 mt-0.5">ℹ️</div>
                  <div className="flex-1 text-xs text-gray-400">
                    <div className="font-medium text-blue-400 mb-1">Estimated Duration</div>
                    <div>
                      Based on your configuration: <span className="text-white font-bold">
                        {formData.scan_type === 'network_discovery' && '15-30 minutes'}
                        {formData.scan_type === 'vulnerability_scan' && '1-2 hours'}
                        {formData.scan_type === 'full_autonomous' && '2-4 hours'}
                      </span>
                    </div>
                    {formData.evasion_enabled && (
                      <div className="mt-1 text-yellow-400">⚠️ Evasion techniques may increase scan duration by 2-3x</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-brand-green/20 sticky bottom-0 bg-brand-black-light">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>

            <div className="text-center">
              <span className="text-xs text-gray-500">Step {currentStep} of {steps.length}</span>
            </div>

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                className="btn-green flex items-center gap-2"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="bg-gradient-to-r from-brand-green to-green-600 text-brand-black px-8 py-3 rounded-lg font-bold hover:from-green-600 hover:to-brand-green transition-all flex items-center gap-2 shadow-lg"
              >
                <Zap className="w-5 h-5" />
                Lancer l'Analyse Experte
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ExpertScanWizard;
